import type { ResumeRoleBullet } from '@careeropt/schema';
import { ORB_HINTS, deriveOrbVisualState, useLivingConversation } from '@careeropt/voice-client';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlignmentReviewDeck } from '../components/AlignmentReviewDeck';
import { Card } from '../components/Card';
import { CorpusArchivePanel } from '../components/CorpusArchivePanel';
import { CorpusCanvas } from '../components/CorpusCanvas';
import { CorpusResumePanel } from '../components/resume/CorpusResumePanel';
import { ImportPasteCard } from '../components/ImportPasteCard';
import { Orb } from '../components/Orb';
import { startAgentSession } from '../lib/api';
import { useAlignmentReview } from '../lib/useAlignmentReview';
import { useCorpusCanvas } from '../lib/useCorpusCanvas';
import { useCorpusResume } from '../lib/useCorpusResume';
import { useDnaOverlay } from '../lib/useDnaOverlay';
import { resolveRealThreads, resolveThreadsDisplayState } from '../lib/refineThreadsSource';
import { resolveRefineDnaReveal } from '../lib/refineDnaReveal';
import {
  readSurfaceCollapsed,
  summarizeThreadsCount,
  writeSurfaceCollapsed,
} from '../lib/refineThreadsCollapse';
import { placeholderRefine } from '../lib/placeholderData';
import { resolveCorpusOpenerState, resolveOpenerLine } from '../lib/refineOpener';
import styles from './Refine.module.css';

/**
 * Refine tab. B-2: the orb is LIVE — useLivingConversation (extracted to
 * @careeropt/voice-client per the contract) drives status/mode/isSpeaking/
 * frequencies for real, wired to the SAME POST /api/agent-session route
 * apps/web's corpus canvas calls (startAgentSession, this app's own
 * apiFetch-bound mint — see lib/api.ts). Tapping the mic button opens a real
 * WebRTC voice session; the caption ribbon shows the live agent/user turns.
 *
 * B-3 adds the REAL corpus canvas + import flow below the voice hero:
 *   - CorpusCanvas: fetches + renders the user's actual roles/achievements
 *     (useCorpusCanvas -> GET /corpus/roles + GET /corpus/achievements),
 *     grouped by role/era, with drag-to-rehome (optimistic + server-confirm +
 *     rollback — see lib/dragRehome.ts for why this deliberately departs from
 *     apps/web's non-optimistic drag pattern).
 *   - ImportPasteCard: the paste-in-text flow (POST /corpus/import), the
 *     mic-icon-bearing textarea per the visual law, honest result counts.
 * Both are READ+WRITE against the real backend; nothing here is placeholder.
 *
 * LANE W-CORPUS wires the two remaining static pieces the header above still
 * documented as placeholder-only:
 *
 *   (1) THE CORPUS DISPLAY — a "read as résumé" toggle per era (see
 *       RoleGroupSection's asResume/onToggleResume), ported 1:1 from apps/
 *       web's Corpus.tsx (resumeEras + servedBulletsByRole join). useCorpusResume
 *       fetches GET /corpus/resume (the SAME résumé endpoint apps/web's own
 *       /corpus résumé view renders); servedBulletsByRole slices its
 *       `experience` section's roles by their additive `roleId`, exactly as
 *       apps/web's Corpus.tsx does, so an era flipped to "Résumé" shows the
 *       served, gated, ghostwritten bullets (or the verbatim headline
 *       fallback) instead of the raw card grid — and, because
 *       RoleResumeSection never reads `.tags`, an era read this way can never
 *       leak a card's internal provenance tags (pkg:enabler-record-v1 /
 *       claim:receipted-fact / src:ET-L61-64 / etc. — issue #44) as visible
 *       chips, regardless of what rides in that array. Cards are one
 *       toggle-click away; nothing is removed. A dedicated "read the WHOLE
 *       corpus as one résumé" panel is also available (see corpusView below).
 *
 *   (2) "THREADS MIRA IS NOTICING" — now the REAL era-to-era continuity the
 *       DNA synthesis found (GET /corpus/dna-overlay -> profile.threads,
 *       dna_profiles.threads jsonb), not the static ['Cross-functional
 *       collaboration', 'Mentorship & growth'] pair. A user with no DNA-
 *       synthesis pass yet (profile: null, or threads: null/empty) sees an
 *       honest "keep talking to Mira" nudge — never the old static pair
 *       silently standing in as if it were real signal.
 *
 * Still STATIC in this brief (unchanged, and correctly so — see each site's
 * own doc comment): d.firstMessage/d.voiceHint (copy-fallback strings for the
 * live caption ribbon, unrelated to this lane), and the "your story in your
 * words" transcript bubble below (reads off the LIVE session's landFacet
 * stream, a later brief's wiring).
 *
 * LANE ROLE-REFINE wires the two remaining gaps against the corpus canvas
 * (parity matrix #6/#7): editing a role's own fields and editing an
 * achievement's own fields, both via useCorpusCanvas's new editRole/
 * editAchievement (PATCH /corpus/roles/:id, PATCH /corpus/achievements/:id —
 * routes that already existed; this is the first Throughline UI to call
 * them). Same canvasError surface the drag-rehome path already uses (one
 * honest error banner for any canvas write failure), and the same
 * corpusResume.reload() nudge after a successful edit — an edited role's
 * dates/title or an edited achievement's headline/kpi can change what the
 * corpus-résumé document composes next time it's requested.
 */
export function Refine() {
  const d = placeholderRefine;
  const [caption, setCaption] = useState<{ source: 'agent' | 'user'; text: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mintRef = useRef(startAgentSession);
  const corpus = useCorpusCanvas();
  const [rehomeError, setRehomeError] = useState<string | null>(null);
  const dna = useDnaOverlay();
  const corpusResume = useCorpusResume();
  const alignmentReview = useAlignmentReview();
  const [alignmentError, setAlignmentError] = useState<string | null>(null);
  // CREW-UX — the alignment-review deck's completion moment offers "Want to
  // see the résumé fresh?"; the résumé view already lives on THIS page (the
  // corpusView toggle below, not a new route), so this ref is only a scroll
  // target for that same-page reveal (see handleAlignmentViewResume).
  const corpusSectionRef = useRef<HTMLDivElement>(null);
  // Additive view toggle: the whole corpus surfaced AS one résumé (DEFAULT —
  // Jason: 'the data being shown is not for human consumption, it should be
  // shown in the resume style from the other front end' + 'get that default
  // resume view … deployed') vs the per-era card canvas. The résumé is the
  // human-readable landing view; the card canvas is one toggle away, never
  // removed. Shape mirrors apps/web's Corpus.tsx corpusView ('graph' | 'resume').
  const [corpusView, setCorpusView] = useState<'cards' | 'resume'>('resume');
  // Per-era "read as résumé": the set of era ids currently rendered as a
  // résumé fragment instead of the card grid. Additive — an era reads as the
  // grid until the user flips it. Mirrors apps/web's resumeEras Set exactly.
  const [resumeEras, setResumeEras] = useState<Set<string>>(() => new Set());

  // Join the served corpus-résumé document back to the eras on the canvas, so
  // a flipped era can render the document's GHOSTWRITTEN bullets (the gated
  // synthesis) instead of the raw card headlines. The document's `experience`
  // roles carry the additive `roleId` (the corpus role they were composed
  // from — see ResumeComposerService); keyed by it, mirrors apps/web's
  // Corpus.tsx servedBulletsByRole memo exactly. NO INVENTION: this only
  // reshapes what the backend already served; if the document isn't loaded,
  // the route isn't live, or a role/roleId is missing, the map simply has no
  // entry and RoleResumeSection falls back to the verbatim headline per card.
  const servedBulletsByRole = useMemo(() => {
    const m = new Map<string, ResumeRoleBullet[]>();
    const doc = corpusResume.document;
    if (!doc) return m;
    const exp = doc.sections.find((s) => s.kind === 'experience');
    if (!exp || exp.kind !== 'experience') return m;
    for (const r of exp.roles) {
      const rid = r.roleId;
      if (rid) m.set(rid, r.bullets);
    }
    return m;
  }, [corpusResume.document]);

  // The REAL "threads Meira is noticing" — era-to-era continuity from the DNA
  // synthesis (dna_profiles.threads), replacing the static placeholderRefine
  // pair. The demo-vs-real source decision is extracted + unit-pinned (lib/
  // refineThreadsSource.ts) rather than inlined: null/empty (no synthesis
  // pass yet, or none found) is a real, honest state, rendered as a nudge
  // below — never silently backfilled with the old static strings.
  const realThreads = resolveRealThreads(dna.state.overlay);
  const threadsDisplayState = resolveThreadsDisplayState(dna.state.loading, dna.state.overlay);

  // LANE DNA-BUILD Phase 0: the through-line prose / positioning spine /
  // origins, on the SAME shelf as "Threads Meira is noticing" — zero new
  // fetch, this reads the SAME dna.state.overlay object already in memory
  // above. Null when there is no synthesis pass yet (honest, never a
  // fabricated placeholder) — see lib/refineDnaReveal.ts's own doc comment.
  const dnaReveal = resolveRefineDnaReveal(dna.state.overlay);

  // MOBILE-SHELL — the owner's live mobile-QA finding: the threads chip
  // row/nudge (NOT the "Your through-line" narrative below it — that stays
  // open, it's the payoff content) defaults COLLAPSED so it doesn't eat the
  // whole viewport on the surface people use ~90% of the time. Read once on
  // mount (no jobId to key on here — this is a single, page-level surface,
  // unlike JobDetail's per-job sections — see refineThreadsCollapse.ts's own
  // header for why that module is a sibling of collapsibleSection.ts rather
  // than a reuse of its job-scoped functions). sessionStorage-remembered for
  // the rest of this tab session, same discipline as JobDetail's own
  // collapseState.
  const [threadsOpen, setThreadsOpen] = useState(() => readSurfaceCollapsed('refine-threads'));

  /** Mirrors JobDetail's handleToggleCollapse: wired to <details>'s own
   *  onToggle (reading e.currentTarget.open) rather than a click handler, so
   *  this stays correct for keyboard (Enter/Space on <summary>) and the
   *  browser's own find-in-page auto-expand, not just a mouse tap. */
  function handleToggleThreads(open: boolean) {
    setThreadsOpen(open);
    writeSurfaceCollapsed('refine-threads', open);
  }

  const conversation = useLivingConversation(mintRef.current, {
    onCaption: ({ source, text }) => setCaption({ source, text }),
    onError: (message) => setErrorMessage(message || 'voice error'),
  });

  const visual = deriveOrbVisualState({
    status: conversation.status,
    mode: conversation.mode,
    isSpeaking: conversation.isSpeaking,
  });
  const live = visual === 'listening' || visual === 'speaking' || visual === 'connecting';

  function handleMicTap() {
    setErrorMessage(null);
    if (live) {
      conversation.stop();
      return;
    }
    conversation.start({ anchorType: 'origins' }).then((id) => {
      if (id === null) setErrorMessage('Could not start the voice session — try again in a moment.');
    });
  }

  function handleDragRehome(cardId: string, targetRoleId: string | null) {
    setRehomeError(null);
    corpus.rehome(cardId, targetRoleId).then((err) => {
      // A successful re-home changes which era a card belongs to, which can
      // change what the corpus-résumé document composes next time it's
      // requested — reload it so a flipped-to-Résumé era doesn't keep showing
      // a stale ghostwritten bullet under its old role. Best-effort: a reload
      // failure here just means the NEXT view of that era falls back to
      // verbatim headlines until the résumé loads again, never a crash.
      if (!err) corpusResume.reload();
      if (err) setRehomeError(err);
    });
  }

  /** Edit a role's own fields (LANE ROLE-REFINE, parity matrix gap #6). Same
   *  contract as handleDragRehome: clear the shared canvas error, run the
   *  optimistic-patch-confirm-rollback save, reload the résumé doc on success
   *  (an edited title/company/dates can change what composes next), surface
   *  the honest error on failure. Returns the error string to the form so it
   *  can also render it inline (RoleEditForm's own error row) without a second
   *  round trip. */
  function handleRoleEdit(roleId: string, patch: Parameters<typeof corpus.editRole>[1]) {
    setRehomeError(null);
    return corpus.editRole(roleId, patch).then((err) => {
      if (!err) corpusResume.reload();
      if (err) setRehomeError(err);
      return err;
    });
  }

  /** Edit an achievement's own fields (LANE ROLE-REFINE, parity matrix gap
   *  #7). Same discipline as handleRoleEdit above, against PATCH /corpus/
   *  achievements/:id. */
  function handleAchievementEdit(
    achievementId: string,
    patch: Parameters<typeof corpus.editAchievement>[1],
  ) {
    setRehomeError(null);
    return corpus.editAchievement(achievementId, patch).then((err) => {
      if (!err) corpusResume.reload();
      if (err) setRehomeError(err);
      return err;
    });
  }

  function handleImported() {
    corpus.refresh();
    // A fresh import can add roles/achievements the corpus-résumé document
    // hasn't seen yet; reload so the next toggle-to-Résumé (or the whole-
    // corpus résumé view) reflects what was just imported, not a stale doc.
    corpusResume.reload();
  }

  /** W3 remove-an-import: after an archive/undo (the CorpusArchivePanel or the
   *  ImportPasteCard's "undo this import"), re-fetch the corpus AND reload the
   *  résumé doc — a removed role/card must vanish from both the canvas and the
   *  composed résumé, the same reason handleImported reloads after an add. */
  function handleCorpusChanged() {
    corpus.refresh();
    corpusResume.reload();
  }

  /**
   * The alignment-review deck's three terminal actions (CLEANUP-CREW). Each
   * is ONE explicit human ruling on ONE flagged card — never a batch, never
   * automatic (the governing law). A 'moved' ruling re-homes the card
   * server-side through the EXISTING §6 supersede (the same write path the
   * drag-rehome canvas uses), so — exactly like handleDragRehome above — a
   * successful move can change what the corpus-résumé document composes
   * next time it's requested; reload it on success. The shared
   * alignmentError banner mirrors rehomeError's own single-honest-surface
   * discipline (one error row for any ruling failure, not a per-card dupe).
   */
  function handleAlignmentMove(achievementId: string, roleId: string) {
    setAlignmentError(null);
    alignmentReview.rule(achievementId, 'moved', roleId).then((err) => {
      if (!err) corpusResume.reload();
      if (err) setAlignmentError(err);
    });
  }

  function handleAlignmentKeep(achievementId: string) {
    setAlignmentError(null);
    alignmentReview.rule(achievementId, 'kept').then((err) => {
      if (err) setAlignmentError(err);
    });
  }

  function handleAlignmentFlagSplit(achievementId: string) {
    setAlignmentError(null);
    alignmentReview.rule(achievementId, 'flagged_split').then((err) => {
      if (err) setAlignmentError(err);
    });
  }

  /**
   * MERGE (RESUME-POLISH lane, part c) — a near_duplicate cluster member is
   * soft-merged INTO `survivorId` (the richest cluster member, picked by the
   * deck the SAME way the résumé composer picks one — see
   * AlignmentReviewCard's own pickClusterSurvivor). Mirrors handleAlignmentMove
   * exactly: a successful merge changes what the corpus-résumé composes next
   * (the merged-away card's text no longer competes for a bullet slot), so
   * reload it on success, same as a move.
   */
  function handleAlignmentMerge(achievementId: string, survivorId: string) {
    setAlignmentError(null);
    alignmentReview.rule(achievementId, 'merged', undefined, survivorId).then((err) => {
      if (!err) corpusResume.reload();
      if (err) setAlignmentError(err);
    });
  }

  /**
   * CREW-UX — the alignment-review deck's completion moment: "Want to see
   * the résumé fresh?" The résumé view is NOT a new route — it's the SAME
   * corpusView toggle the "Cards / Résumé" pair above already drives (LANE
   * W-CORPUS wiring target #1). This handler only switches that existing
   * toggle to 'resume' and scrolls the same-page section into view; the deck
   * itself owns no view-toggle state (no-frontend-bandaids: the state that
   * already exists here is reused, never duplicated inside the deck).
   */
  function handleAlignmentViewResume() {
    setCorpusView('resume');
    corpusSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // HIS 2026-07-20 WALK (NO INVENTION): the opener is grounded in the real
  // corpus state — empty asks for the resume honestly; the old static
  // "I just finished reading your resume" claim is gone (refineOpener.ts).
  const openerLine = resolveOpenerLine(
    resolveCorpusOpenerState(corpus.state.loading, corpus.state.roles.length, corpus.state.achievements.length),
  );
  const captionText = caption?.text ?? openerLine;
  const captionLabel = caption ? (caption.source === 'agent' ? 'Meira says' : 'You said') : 'Meira asks';
  const hint = errorMessage ?? ORB_HINTS[visual] ?? d.voiceHint;

  return (
    <div className={styles.page}>
      <div className={styles.eyebrow}>
        <div className={styles.kicker}>Talk it through</div>
        <div className={styles.title}>with Meira</div>
      </div>

      <div className={styles.voiceHero}>
        <div className={styles.caption}>
          <div className={styles.captionLabel}>{captionLabel}</div>
          <div className={styles.captionText}>{captionText}</div>
        </div>

        <div className={styles.orbWrap}>
          <Orb
            status={conversation.status}
            mode={conversation.mode}
            isSpeaking={conversation.isSpeaking}
            frequencies={conversation.frequencies}
          />
        </div>

        <button
          type="button"
          className={styles.micButton}
          onClick={handleMicTap}
          aria-pressed={live}
          data-tour="refine-mic-button"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect
              x="9"
              y="3"
              width="6"
              height="11"
              rx="3"
              stroke="currentColor"
              strokeWidth="1.9"
            />
            <path
              d="M5 11a7 7 0 0 0 14 0M12 18v3"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
            />
          </svg>
          {live ? 'Tap to stop' : 'Tap to speak'}
        </button>

        <div className={styles.voiceHint} data-error={errorMessage != null}>
          {hint}
        </div>
      </div>

      <Card className={styles.threadsCard}>
        {/* MOBILE-SHELL — collapsed by default (real estate the owner's live
            mobile walk flagged: "we need as much screen real estate as we
            can on mobile"), same native <details>/<summary> idiom JobDetail's
            informational cards already establish (R279). The dot+title
            header rides INSIDE <summary> so the whole row is the tap target,
            matching <summary>'s own native affordance; only the count badge
            sits outside it so it can render even mid-transition without
            fighting <summary>'s default marker layout. */}
        <details
          open={threadsOpen}
          onToggle={(e) => handleToggleThreads(e.currentTarget.open)}
        >
          <summary className={styles.threadsSummary} style={{ cursor: 'pointer' }}>
            <span className={styles.threadsHeader}>
              <span className={styles.threadsDot} />
              <span className={styles.threadsTitle}>Threads Meira is noticing</span>
            </span>
            {/* NO INVENTION: this count is realThreads?.length — the served
                DNA-overlay array's own size, never a hardcoded or guessed
                number. threadsDisplayState !== 'real' (loading, or no
                synthesis pass yet) has no honest count to report, so the
                fallback copy renders instead of a fabricated "0 threads". */}
            <span className={styles.threadsSummaryCount}>
              {summarizeThreadsCount(threadsDisplayState === 'real' ? (realThreads?.length ?? null) : null)}
            </span>
          </summary>
          <div className={styles.threadsBody}>
            {threadsDisplayState === 'loading' ? (
              <div className={styles.threadsNudge}>Reading what carries across your eras…</div>
            ) : threadsDisplayState === 'real' && realThreads ? (
              <div className={styles.chipRow}>
                {realThreads.map((th, i) => (
                  <span key={`${th.from}-${th.to}-${i}`} className={styles.chip}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {th.thread}
                  </span>
                ))}
              </div>
            ) : (
              // Honest empty state — no DNA-synthesis pass yet (or none found
              // any continuity), never a fabricated stand-in. Talking to Mira
              // above is the real on-ramp that produces this signal.
              <div className={styles.threadsNudge}>
                Keep talking with Meira — the threads across your roles will show up here as she finds them.
              </div>
            )}
          </div>
        </details>
        {/* LANE DNA-BUILD Phase 0: the through-line prose / positioning spine
            / origins, same shelf, same card, additive content only — render-
            only, zero new fetch (see resolveRefineDnaReveal's doc comment).
            Renders nothing at all when dnaReveal is null, so a user with no
            synthesis pass yet sees only the threads nudge above, never an
            empty section header standing in for missing signal. */}
        {dnaReveal ? (
          <div className={styles.dnaReveal}>
            {dnaReveal.throughLine ? (
              <div className={styles.dnaRevealSection}>
                <div className={styles.dnaRevealLabel}>Your through-line</div>
                <p className={styles.dnaRevealBody}>{dnaReveal.throughLine}</p>
              </div>
            ) : null}
            {dnaReveal.positioningSpine ? (
              <div className={styles.dnaRevealSpine}>
                <div className={styles.dnaRevealSpineLabel}>Positioning spine</div>
                <p className={styles.dnaRevealSpineBody}>{dnaReveal.positioningSpine}</p>
              </div>
            ) : null}
            {dnaReveal.origins ? (
              <div className={styles.dnaRevealSection}>
                <div className={styles.dnaRevealLabel}>Origins</div>
                <p className={styles.dnaRevealBody}>{dnaReveal.origins}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* CLEANUP-CREW gating pass: the achievement-alignment review deck is
          now ALWAYS rendered — it gates under 'tailoring' (alignment.routes
          .ts) and shows an honest state whichever way the fetch resolves
          (blocked / checking / zero-flags resting / flags-list), with its
          own "Check again" re-run affordance wired straight to
          useAlignmentReview's refresh(). Never invisible-by-omission the way
          the empty state used to be — see AlignmentReviewDeck's own header. */}
      <AlignmentReviewDeck
        flags={alignmentReview.state.flags}
        roles={corpus.state.roles}
        loading={alignmentReview.state.loading}
        fetchError={alignmentReview.state.error}
        entitlementBlock={alignmentReview.state.entitlementBlock}
        pendingAchievementId={alignmentReview.rulingState.pendingAchievementId}
        onMove={handleAlignmentMove}
        onKeep={handleAlignmentKeep}
        onFlagSplit={handleAlignmentFlagSplit}
        onMerge={handleAlignmentMerge}
        error={alignmentError}
        onCheckAgain={alignmentReview.refresh}
        onViewResume={handleAlignmentViewResume}
      />

      <Link to="/refine/interview" className={styles.interviewCard} data-tour="refine-interview-card">
        <div className={styles.interviewIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M8 9h8M8 13h5"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
            />
            <rect x="3" y="5" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="1.9" />
            <path d="M9 17l-2 3v-3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <div className={styles.interviewCardTitle}>Answer a few questions</div>
          {/* Deliberately distinct wording from the mic hero's "Tap to speak"
              above — this is the OTHER door into the same DNA data (the
              turn-based dna_sessions loop), never a duplicate label for the
              live-agent conversation. See docs/design/
              dna-interview-port-2026-07-05.md §2 point 2/§3 Phase 1 "Entry
              point" for why the two stay honestly distinct. */}
          <p className={styles.interviewCardBody}>
            Origins, timeline, the pattern underneath — question by question, voice or text.
          </p>
        </div>
      </Link>

      <div>
        <div className={styles.transcriptLabel}>Your story, in your words</div>
        <div className={styles.messageRow}>
          <div className={styles.avatar}>M</div>
          <div className={styles.bubble}>{openerLine}</div>
        </div>
      </div>

      <div className={styles.canvasSection} ref={corpusSectionRef}>
        <div className={styles.canvasSectionHead}>
          <div className={styles.transcriptLabel}>What you've told me so far</div>
          {corpus.state.roles.length > 0 || corpus.state.achievements.length > 0 ? (
            <div className={styles.viewToggle} role="group" aria-label="Story view" data-tour="refine-view-toggle">
              <button
                type="button"
                className={styles.viewToggleBtn}
                aria-pressed={corpusView === 'cards'}
                onClick={() => setCorpusView('cards')}
              >
                Cards
              </button>
              <button
                type="button"
                className={styles.viewToggleBtn}
                aria-pressed={corpusView === 'resume'}
                onClick={() => setCorpusView('resume')}
              >
                Résumé
              </button>
            </div>
          ) : null}
        </div>
        {/* LANE STORY-TOUR (his 2026-07-20 walk): the wrapper carries the
            paste-resume step's anchor — the ring highlights the whole paste
            card; ImportPasteCard itself stays untouched. */}
        <div data-tour="refine-paste-box">
          <ImportPasteCard onImported={handleImported} />
        </div>
        {rehomeError ? (
          <div className={styles.canvasError} role="alert">
            {rehomeError}
          </div>
        ) : null}
        {corpusView === 'resume' ? (
          <CorpusResumePanel
            doc={corpusResume.document}
            resumeId={corpusResume.resumeId}
            loading={corpusResume.loading}
            error={corpusResume.error}
            onReload={corpusResume.reload}
            writtenCut={corpusResume.writtenCut}
            cut={corpusResume.cut}
            hasWritten={corpusResume.hasWritten}
            onSetCut={corpusResume.setCut}
            writing={corpusResume.writing}
            writeError={corpusResume.writeError}
            onWriteCut={corpusResume.writeCut}
          />
        ) : (
          <CorpusCanvas
            roles={corpus.state.roles}
            achievements={corpus.state.achievements}
            loading={corpus.state.loading}
            error={corpus.state.error}
            dragState={corpus.dragState}
            onRehome={handleDragRehome}
            resumeEras={resumeEras}
            onToggleResumeEra={(roleId) =>
              setResumeEras((cur) => {
                const next = new Set(cur);
                next.has(roleId) ? next.delete(roleId) : next.add(roleId);
                return next;
              })
            }
            servedBulletsByRole={servedBulletsByRole}
            onSaveRoleEdit={handleRoleEdit}
            savingRoleId={corpus.roleEditState.pendingId}
            onSaveAchievementEdit={handleAchievementEdit}
            savingAchievementId={corpus.achievementEditState.pendingId}
          />
        )}

        {/* W3 remove-an-import: his live-test need — "delete import if it's the
            wrong file." Pick the wrong roles/cards → archive; undo anything from
            the greyed archived section. Renders nothing when there is nothing to
            manage (its own null guard). */}
        <CorpusArchivePanel
          roles={corpus.state.roles}
          achievements={corpus.state.achievements}
          onChanged={handleCorpusChanged}
        />
      </div>
    </div>
  );
}
